// The `activity` route (`/transactions`, design `#s05` feed). One feed
// grouped by day over every account; the shell's global account filter
// narrows it (`useShellContext().accountFilter`). Cursor-based infinite
// loading via `useTransactions` (`useInfiniteQuery`); the "Likely transfer?"
// review card sits in the feed at the date of its later leg.
import { useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Skeleton } from '@moneyskyline/ui';
import { useShellContext } from '@/app/shell/AppShell';
import { RouteRecoveryScreen } from '@/app/states/RouteStates';
import { HonestInline } from '@/components/HonestInline';
import { useTransactions } from '@/api/hooks/useTransactions';
import { AccountScopeUnavailableError } from '@/api/hooks/accountScope';
import { useLinkCandidates } from '@/api/hooks/useLinkCandidates';
import { useConfirmLink } from '@/api/hooks/useConfirmLink';
import { CategorizationTools } from './tools/CategorizationTools';
import { JobStatusRow } from './tools/JobStatusRow';
import { FeedDayGroup } from './FeedDayGroup';
import { TransferReviewCard } from './TransferReviewCard';
import { ViewSwitcher } from './ViewSwitcher';
import { titleCaseFromSlug } from './spendAggregation';
import type {
  CanonicalTransactionRow,
  PrimaryTransactionsMeta,
  ParsedPrimaryTransactionsPage,
  TransactionRow,
} from '@/api/hooks/useTransactions';
import type { LinkCandidate } from '@/api/hooks/useLinkCandidates';
import { getScopeDateBounds } from './transactionDateRange';
import { scopeHref } from '@/app/shell/scope';
import { DateScopeGuard } from '@/app/shell/DateScopeGuard';
import { candidateInTransactionScope, matchesCategoryFilter, matchesMerchantFilter } from './transactionDrillIn';
import { classifyReviewLaunch, type ReviewLaunchEligibility } from '@/features/reconciliation/reviewLaunch';
import { ReconciliationReviewPanel, type ReconciliationLaunch } from '@/features/reconciliation/ReconciliationReviewPanel';

type CanonicalFeedPage = Extract<ParsedPrimaryTransactionsPage, { kind: 'canonical' }>;
type CanonicalFeedMeta = CanonicalFeedPage['meta'];
type TransactionsQueryPage = {
  data: Array<CanonicalTransactionRow | TransactionRow>;
  meta: PrimaryTransactionsMeta;
};

function isAccountScopeUnavailableError(error: unknown): boolean {
  if (error instanceof AccountScopeUnavailableError) return true;
  return typeof error === 'object' && error !== null && 'reason' in error && error.reason === 'multi_account_scope_unavailable';
}

function isParsedPrimaryPage(page: unknown): page is ParsedPrimaryTransactionsPage | TransactionsQueryPage {
  if (typeof page !== 'object' || page === null) return false;
  if ('kind' in page && (page.kind === 'canonical' || page.kind === 'initialization_required')) return true;
  if (!('data' in page) || !Array.isArray(page.data) || !('meta' in page) || typeof page.meta !== 'object' || page.meta === null) return false;
  const initialization = 'reconciliation_initialization_required' in page.meta && page.meta.reconciliation_initialization_required === true;
  if (!initialization && !('completeness' in page.meta)) return false;
  const canonicalRows = page.data.length === 0 || page.data.every((row) =>
    typeof row === 'object' && row !== null && 'canonical_transaction_id' in row,
  );
  return initialization || canonicalRows;
}

function normalizePrimaryPage(page: ParsedPrimaryTransactionsPage | TransactionsQueryPage): ParsedPrimaryTransactionsPage {
  if ('kind' in page) return page;
  if (page.meta.reconciliation_initialization_required === true) {
    return { kind: 'initialization_required', data: page.data, meta: { ...page.meta, reconciliation_initialization_required: true } };
  }
  return { kind: 'canonical', data: page.data as CanonicalTransactionRow[], meta: page.meta };
}

function dayKey(isoDate: string): string {
  return isoDate.slice(0, 10);
}

function dayLabel(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function postedDayLabel(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function groupByDay(rows: CanonicalTransactionRow[], usePostedDay: boolean): { key: string; label: string; rows: CanonicalTransactionRow[] }[] {
  const groups = new Map<string, CanonicalTransactionRow[]>();
  for (const row of rows) {
    const key = usePostedDay ? row.posted_day : dayKey(row.posted_at);
    if (typeof key !== 'string') {
      throw new Error('confirmed transaction rows require a server-authoritative posted_day');
    }
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([key, groupRows]) => ({
      key,
      label: usePostedDay ? postedDayLabel(key) : dayLabel(groupRows[0].posted_at),
      rows: groupRows,
    }));
}

/** Groups unconfirmed candidates by the day of their LATER leg. */
function candidatesByDay(candidates: LinkCandidate[]): Map<string, LinkCandidate[]> {
  const map = new Map<string, LinkCandidate[]>();
  for (const candidate of candidates) {
    const laterLeg = candidate.legs[0].posted_at > candidate.legs[1].posted_at ? candidate.legs[0] : candidate.legs[1];
    const key = dayKey(laterLeg.posted_at);
    const bucket = map.get(key);
    if (bucket) bucket.push(candidate);
    else map.set(key, [candidate]);
  }
  return map;
}

function historyNotice(meta: CanonicalFeedMeta | undefined): string | null {
  if (!meta) return null;
  if (meta.completeness.history_status === 'failed') return 'Transaction history could not be fully loaded. Some history may be missing.';
  if (meta.completeness.history_status === 'loading') return 'Transaction history is still loading.';
  if (meta.completeness.completeness === 'incomplete') return 'Transaction history is incomplete. More history may still be loading.';
  return null;
}

function AccountReviewHost({
  launch,
  location,
  onViewProgress,
}: {
  launch: ReconciliationLaunch;
  location: { search: string; hash: string };
  onViewProgress: () => void;
}) {
  const returnFocusRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const launchKey = `${launch.canonicalAccountId}:${launch.replacementItemId}`;
  return (
    <div className="w-full" data-testid="reconciliation-review-host">
      {/* ENG-F-001: the review-eligible-account header is a SEPARATE branch
          from TransactionsPage's normal header, but it must render the same
          single shared "Categorization tools" entry -- Reclassify and "Tell
          me a rule" must not disappear on an account that also has a
          reconciliation review pending. */}
      <header className="flex items-center justify-between gap-3">
        <h1 id="activity-heading" tabIndex={-1} className="text-title font-semibold">Transactions</h1>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <button
            ref={returnFocusRef}
            type="button"
            className="min-h-11 inline-flex items-center text-sm text-primary underline"
            onClick={() => setOpen(true)}
          >
            Review account changes
          </button>
          <CategorizationTools onViewProgress={onViewProgress} />
          <Link to={scopeHref(`/transactions/inbox${location.hash}`, location.search)} className="min-h-11 inline-flex items-center text-sm text-primary underline">Review</Link>
        </div>
      </header>
      <div className="mt-3 w-full" data-testid="reconciliation-review-panel-host">
        <ReconciliationReviewPanel
          key={launchKey}
          launch={launch}
          open={open}
          onOpenChange={setOpen}
          returnFocusRef={returnFocusRef}
        />
      </div>
    </div>
  );
}

function TransactionsPageContent() {
  const {
    accountFilter,
    accountLabel,
    accountIds,
    accountScopeAvailability,
    dateFrom: scopeFrom,
    dateTo: scopeTo,
    scopeAvailability,
    retryScopeAvailability,
    canonicalAccounts,
  } = useShellContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get('category');
  const merchantFilter = searchParams.get('merchant');
  const { from: dateFrom, to: dateTo } = getScopeDateBounds({ dateFrom: scopeFrom, dateTo: scopeTo });
  const accountScopeReady = accountScopeAvailability === undefined || accountScopeAvailability === 'ready';
  const canonicalReady = scopeAvailability === 'ready' && canonicalAccounts !== undefined && accountScopeReady;
  const transactions = useTransactions({ accountIds, dateFrom, dateTo, enabled: canonicalReady });
  const accountScopeError = isAccountScopeUnavailableError(transactions.error);
  const temporalUnavailable = transactions.temporal?.availability === 'unavailable'
    && transactions.temporal.reason !== 'awaiting_response'
    && transactions.temporal.reason !== 'metadata_absent';
  const candidatesQuery = useLinkCandidates(canonicalReady && !accountScopeError && !temporalUnavailable);
  const confirmLink = useConfirmLink();
  const [pendingCandidateIds, setPendingCandidateIds] = useState<Set<string>>(() => new Set());
  const [candidateErrors, setCandidateErrors] = useState<Record<string, string>>({});

  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (isOffline) return <RouteRecoveryScreen kind="offline" frame="inline" />;

  if (accountScopeAvailability === 'unavailable' || accountScopeError) {
    return (
      <div className="mt-4 space-y-2">
        <HonestInline kind="api-unavailable" />
        <p className="text-sm text-muted-foreground" data-testid="account-scope-unavailable-copy">
          Multiple selected accounts are not available yet. Choose one account or All accounts to view transactions.
        </p>
      </div>
    );
  }

  if (scopeAvailability !== 'ready') {
    return (
      <div className="mx-auto w-full max-w-5xl p-4">
        {scopeAvailability === 'pending' ? (
          <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">Checking transaction scope availability…</p>
        ) : (
          <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">
            Transaction scope availability could not be checked.{' '}
            <button type="button" className="min-h-11 rounded-md border border-input px-3 underline" onClick={retryScopeAvailability}>Retry</button>
          </p>
        )}
      </div>
    );
  }

  if (!canonicalReady) {
    return <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history is unavailable until canonical data is ready.</p>;
  }

  if (temporalUnavailable) {
    return (
      <div className="mt-4 space-y-2">
        <HonestInline kind="api-unavailable" />
        <p className="text-sm text-muted-foreground" data-testid="temporal-scope-unavailable-copy">
          Transaction history is unavailable because its saved-timezone scope could not be confirmed.
        </p>
      </div>
    );
  }

  if (transactions.isPending) {
    return (
      <div className="mt-4 space-y-2" data-testid="feed-loading-skeleton">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }

  if (transactions.isError) return <RouteRecoveryScreen kind="api-unavailable" frame="inline" />;

  const pages = transactions.data?.pages ?? [];
  if (!transactions.data || pages.length === 0) {
    return <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history is unavailable because no canonical history page was returned.</p>;
  }
  const parsedPages = pages.filter(isParsedPrimaryPage).map(normalizePrimaryPage);
  if (parsedPages.length !== pages.length) {
    return <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history is unavailable because no canonical history page was returned.</p>;
  }
  const initializationPage = parsedPages.find((page) => page.kind === 'initialization_required');
  if (initializationPage) {
    return <p role="status" className="mt-4 rounded-md border border-degraded-border bg-degraded-surface p-4 text-sm text-degraded-foreground">Transaction history needs setup. Feed is unavailable.</p>;
  }

  const canonicalPages = parsedPages.filter((page): page is CanonicalFeedPage => page.kind === 'canonical');
  const allRows = canonicalPages.flatMap((page) => page.data);
  const rows = allRows.filter((row) =>
    (!categoryFilter || matchesCategoryFilter(row, categoryFilter)) &&
    (!merchantFilter || matchesMerchantFilter(row, merchantFilter)),
  );
  const lastMeta = canonicalPages[canonicalPages.length - 1]?.meta;
  const notice = historyNotice(lastMeta);
  const temporalMetadataAbsent = transactions.temporal?.availability === 'unavailable'
    && transactions.temporal.reason === 'metadata_absent';
  const candidates = (candidatesQuery.data ?? []).filter((candidate) => candidateInTransactionScope(candidate, {
    accountId: accountIds?.length === 1 ? accountIds[0] : undefined,
    dateFrom,
    dateTo,
  }));
  const candidateMap = candidatesByDay(candidates);
  const groups = groupByDay(rows, transactions.temporal?.availability === 'confirmed');
  const groupByKey = new Map(groups.map((group) => [group.key, group]));
  const dayKeys = [...new Set([...groups.map((group) => group.key), ...candidateMap.keys()])]
    .sort((a, b) => (a < b ? 1 : -1));
  const merchantLabel = merchantFilter
    ? merchantFilter.startsWith('raw:')
      ? merchantFilter.slice(4) || 'Selected merchant'
      : allRows.find((row) => row.merchant?.merchant_id === merchantFilter)?.merchant?.display_name ?? 'Selected merchant'
    : null;
  const formatScopeDate = (value: string) => new Date(value).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  const intervalLabel = dateFrom && dateTo ? `${formatScopeDate(dateFrom)} – ${formatScopeDate(dateTo)}` : 'All dates';

  const confirmCandidate = async (candidateId: string) => {
    setPendingCandidateIds((current) => new Set(current).add(candidateId));
    setCandidateErrors((current) => {
      const next = { ...current };
      delete next[candidateId];
      return next;
    });
    try {
      await confirmLink.mutateAsync({ candidateId, kind: 'transfer' });
    } catch {
      setCandidateErrors((current) => ({ ...current, [candidateId]: 'The transfer could not be confirmed. Try again.' }));
    } finally {
      setPendingCandidateIds((current) => {
        const next = new Set(current);
        next.delete(candidateId);
        return next;
      });
    }
  };

  const hasDisconnectedHistory = canonicalAccounts.some((account) =>
    account.status_detail === 'connection_disconnected' && (!accountFilter || account.canonical_account_id === accountFilter),
  );

  return (
    <div>
      {/* Scope line (spec §2 item 2): real interval + account. */}
      <div className="mt-1 text-xs text-muted-foreground" data-testid="feed-scope-header">Selected interval: {intervalLabel} · {accountLabel ?? (accountFilter ? 'Selected account' : 'All accounts')}{transactions.temporal?.availability === 'confirmed' ? ' (saved timezone)' : ''}</div>
      {temporalMetadataAbsent && (
        <div className="mt-3 space-y-2">
          <HonestInline kind="api-unavailable" />
          <p className="text-sm text-muted-foreground" data-testid="temporal-scope-unavailable-copy">
            Rows are shown, but saved-timezone calendar grouping is unavailable.
          </p>
        </div>
      )}
      {hasDisconnectedHistory && <p role="status" className="mb-3 rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm text-degraded-foreground">Saved history from a disconnected bank connection. New transactions are not syncing for that connection.</p>}
      {notice && <p role="status" className="mb-3 rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm text-degraded-foreground">{notice}</p>}
      {!notice && candidatesQuery.isPending && <p role="status" className="mb-3 rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">Transfer suggestions are loading…</p>}
      {candidatesQuery.isError && <p role="status" className="mb-3 rounded-md border border-degraded-border bg-degraded-surface p-3 text-sm text-degraded-foreground">Transfer suggestions could not be loaded. Transactions are still shown.{' '}<button type="button" className="mt-2 min-h-11 rounded-md border border-input px-3 underline" onClick={() => candidatesQuery.refetch()} disabled={candidatesQuery.isFetching}>{candidatesQuery.isFetching ? 'Retrying…' : 'Retry transfer suggestions'}</button></p>}
      {(categoryFilter || merchantFilter) && <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground" data-testid="category-filter-banner"><span>Filtered by {categoryFilter ? titleCaseFromSlug(categoryFilter) : merchantLabel}</span><Link to={scopeHref(`/transactions${location.hash}`, location.search)} className="min-h-9 inline-flex items-center text-primary underline">Clear</Link></div>}

      {groups.length === 0 ? (
        transactions.hasNextPage ? (
          <p className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground" data-testid="feed-awaiting-filtered-pages">No matching transactions are loaded yet. More transactions are available.</p>
        ) : <RouteRecoveryScreen kind="no-data-in-range" frame="inline" />
      ) : (
        <div data-testid="feed-groups">
          {dayKeys.map((dayKeyValue) => {
            const group = groupByKey.get(dayKeyValue);
            return (
              <div key={dayKeyValue} data-day-key={dayKeyValue}>
                {group && <FeedDayGroup label={group.label} rows={group.rows} />}
                {(candidateMap.get(dayKeyValue) ?? []).map((candidate) => <TransferReviewCard key={candidate.candidate_id} candidate={candidate} confirming={pendingCandidateIds.has(candidate.candidate_id)} error={candidateErrors[candidate.candidate_id]} onConfirm={confirmCandidate} />)}
              </div>
            );
          })}
        </div>
      )}

      {transactions.hasNextPage && <><p className="mt-3 text-xs text-muted-foreground" data-testid="feed-partial-scope">More transactions are available.</p><button type="button" onClick={() => transactions.fetchNextPage()} disabled={transactions.isFetchingNextPage} className="mt-1 min-h-11 w-full rounded-md border border-input py-2 text-sm" data-testid="feed-load-more">{transactions.isFetchingNextPage ? 'Loading…' : 'Load more'}</button></>}
    </div>
  );
}

export function TransactionsPage() {
  const { canonicalAccounts, dateScopeAvailability, scopeAvailability } = useShellContext();
  const location = useLocation();
  // "View progress" (from the tools menu's running-job item, and from the
  // status row's own button) focuses the always-visible JobStatusRow below
  // rather than opening a second progress surface -- §5 keeps a single
  // running-job status source of truth.
  const jobStatusRowRef = useRef<HTMLDivElement>(null);
  const handleViewProgress = () => {
    jobStatusRowRef.current?.focus();
    jobStatusRowRef.current?.scrollIntoView({ block: 'nearest' });
  };
  const normalizedAccountIds = [...new Set(new URLSearchParams(location.search).getAll('account').map((value) => value.trim()).filter(Boolean))];
  const hasConflictingAccountFilters = normalizedAccountIds.length > 1;
  const selectedAccountId = normalizedAccountIds.length === 1 ? normalizedAccountIds[0] : null;
  const selectedCanonicalAccounts = selectedAccountId ? canonicalAccounts?.filter((account) =>
    account.canonical_account_id === selectedAccountId ||
    (account.canonical_account_id === null && account.account_id === selectedAccountId),
  ) ?? [] : [];
  const selectedCanonicalAccount = selectedCanonicalAccounts.length === 1 ? selectedCanonicalAccounts[0] : undefined;
  const reviewEligibility: ReviewLaunchEligibility | null = scopeAvailability === 'ready' && canonicalAccounts !== undefined
    ? hasConflictingAccountFilters || selectedCanonicalAccounts.length > 1
      ? { kind: 'ambiguous' }
      : selectedCanonicalAccount ? classifyReviewLaunch(selectedCanonicalAccount) : { kind: 'no_target' }
    : null;

  const reviewMessage = reviewEligibility?.kind === 'no_target' && selectedCanonicalAccount
    ? 'Account review is not available for this account.'
    : reviewEligibility?.kind === 'ambiguous'
      ? 'Account review needs a single active connection.'
      : reviewEligibility?.kind === 'setup'
        ? 'Account review needs setup.'
        : null;

  return (
    <section aria-labelledby="activity-heading" className="mx-auto w-full max-w-5xl p-4">
      {reviewEligibility?.kind === 'eligible' ? (
        <AccountReviewHost
          key={`${reviewEligibility.launch.canonicalAccountId}:${reviewEligibility.launch.replacementItemId}`}
          launch={reviewEligibility.launch}
          location={location}
          onViewProgress={handleViewProgress}
        />
      ) : (
        <header className="flex items-center justify-between gap-3">
          <h1 id="activity-heading" tabIndex={-1} className="text-title font-semibold">Transactions</h1>
          {/* Title row (spec §2 item 1): "Transactions" at left, one
              "Categorization tools" entry at right -- 44px height. The prior
              duplicate page-level "Review" link and the always-visible
              Reclassify button + rule composer are gone: bulk tools now live
              behind this one on-demand entry, alongside the account-review
              Review link. */}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {reviewMessage && <span className="text-sm text-muted-foreground">{reviewMessage}</span>}
            <CategorizationTools onViewProgress={handleViewProgress} />
            <Link to={scopeHref(`/transactions/inbox${location.hash}`, location.search)} className="min-h-11 inline-flex items-center text-sm text-primary underline">Review</Link>
          </div>
        </header>
      )}
      {/* Local navigation (spec §2 item 3): text links + underline, reused ViewSwitcher. */}
      <ViewSwitcher />
      {/* Status slot (spec §2 item 4): compact job status, only when a job
          is known. Focusable so "View progress" from the tools menu can
          move focus/scroll here instead of opening a second surface. */}
      <div ref={jobStatusRowRef} tabIndex={-1}>
        <JobStatusRow onViewProgress={handleViewProgress} />
      </div>
      <DateScopeGuard availability={dateScopeAvailability}><TransactionsPageContent /></DateScopeGuard>
    </section>
  );
}

export default TransactionsPage;
